import { useEffect } from "react";
import { toast } from "react-toastify";

/**
 * Registra todos los listeners Socket.IO de la página Connections.
 * Contiene tres useEffects que originalmente vivían en index.js:
 *   1. Listener principal (whatsapp + whatsappSession + connect/disconnect)
 *   2. Listener secundario de actualización (PLAN_LIMIT_REACHED, badSession, reconnected)
 *   3. Listener de alertas de recursos del servidor
 *
 * Los refs (qrModalOpenRef, etc.) se usan para la lógica interna sin provocar
 * re-registros de los listeners cuando el estado cambia. Los state values
 * (qrModalOpen, etc.) se mantienen en los deps arrays para preservar el
 * comportamiento original del componente.
 */
const useConnectionsSocket = ({
  socketManager,
  companyId,
  refreshWhatsApps,
  handleCleanAndReconnect,
  showResourceNotification,
  whatsApps,
  // State values — solo para los deps arrays (la lógica usa refs)
  qrModalOpen,
  selectedWhatsApp,
  reconnectModalOpen,
  reconnectingWhatsApp,
  // Refs — acceso estable sin re-registro de listeners
  qrModalOpenRef,
  selectedWhatsAppRef,
  reconnectModalOpenRef,
  reconnectingWhatsAppRef,
  qrErrorToastShown,
  // Setters — estables (setState), no van en deps
  setQrModalOpen,
  setSelectedWhatsApp,
  setReconnectModalOpen,
  setReconnectingWhatsApp,
  setLastAutoRefresh,
  setResourceAlerts,
}) => {

  // WEBSOCKET PRINCIPAL: sincronización en tiempo real
  useEffect(() => {
    if (!socketManager) return;

    const socketCompanyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(socketCompanyId);

    // Un único handler para company-whatsapp — evita que ManagedSocket desduplique
    const handleWhatsAppEvent = (data) => {
      if (!data) return;

      if (data.action === "error" && data.error?.code === "PLAN_LIMIT_REACHED") {
        toast.error(data.error.message || "Has alcanzado el límite de conexiones de tu plan.", { autoClose: 8000 });
        setTimeout(() => refreshWhatsApps(), 1000);
        if (qrModalOpenRef.current && data.whatsapp && selectedWhatsAppRef.current?.id === data.whatsapp.id) {
          setQrModalOpen(false);
          setSelectedWhatsApp(null);
        }
        return;
      }

      if (data.action === "update" && data.reason === "RECONNECTION_TIMEOUT" && data.message) {
        if (window.showNotification) window.showNotification(data.message, "warning");
        else toast.warning(data.message);
      }

      if (data.action === "create") setTimeout(() => refreshWhatsApps(), 1000);
      if (data.action === "update") setTimeout(() => refreshWhatsApps(), 500);
      if (data.action === "delete") setTimeout(() => refreshWhatsApps(), 500);

      if (data.action === "connected" && data.whatsapp?.id) {
        const whatsapp = data.whatsapp;
        refreshWhatsApps();
        if (qrModalOpenRef.current && selectedWhatsAppRef.current?.id === whatsapp.id) {
          setQrModalOpen(false);
          setSelectedWhatsApp(null);
        }
        if (reconnectModalOpenRef.current && reconnectingWhatsAppRef.current?.id === whatsapp.id) {
          setReconnectModalOpen(false);
          setReconnectingWhatsApp(null);
        }
      }

      if (data.action === "reconnected" && data.whatsapp?.id) {
        const whatsapp = data.whatsapp;
        refreshWhatsApps();
        if (reconnectModalOpenRef.current && reconnectingWhatsAppRef.current?.id === whatsapp.id) {
          setReconnectModalOpen(false);
          setReconnectingWhatsApp(null);
        }
      }
    };

    const handleWhatsAppSession = (data) => {
      if (!data || data.action !== "update" || !data.session?.id) return;

      if (data.error) {
        if (data.error.code === "QR_EXPIRED" || data.error.code === "MAX_QR_RETRIES_REACHED") {
          const errorKey = `${data.session.id}-${data.error.code}`;
          const lastShown = qrErrorToastShown.current.get(errorKey);
          const now = Date.now();
          if (!lastShown || now - lastShown > 5000) {
            qrErrorToastShown.current.set(errorKey, now);
            toast.info(data.error.message || "El código QR ha expirado.", { autoClose: 5000 });
          }
          if (qrModalOpenRef.current && selectedWhatsAppRef.current?.id === data.session.id) {
            setQrModalOpen(false);
            setSelectedWhatsApp(null);
          }
          setTimeout(() => refreshWhatsApps(), 1000);
          return;
        }
      }

      refreshWhatsApps();

      const session = data.session;
      if (session.status === "CONNECTED" && session.qrcode === "") {
        if (qrModalOpenRef.current && selectedWhatsAppRef.current?.id === session.id) {
          setQrModalOpen(false);
          setSelectedWhatsApp(null);
          refreshWhatsApps();
          setLastAutoRefresh(Date.now());
        }
        if (reconnectModalOpenRef.current && reconnectingWhatsAppRef.current?.id === session.id) {
          setReconnectModalOpen(false);
          setReconnectingWhatsApp(null);
        }
      }
    };

    const handleConnect = () => {
      setTimeout(() => refreshWhatsApps(), 1000);
      socket.emit("joinNotification");
    };

    socket.on(`company-${socketCompanyId}-whatsapp`, handleWhatsAppEvent);
    socket.on(`company-${socketCompanyId}-whatsappSession`, handleWhatsAppSession);
    socket.on("connect", handleConnect);

    return () => {
      socket.off(`company-${socketCompanyId}-whatsapp`, handleWhatsAppEvent);
      socket.off(`company-${socketCompanyId}-whatsappSession`, handleWhatsAppSession);
      socket.off("connect", handleConnect);
    };
  }, [socketManager, refreshWhatsApps, qrModalOpen, selectedWhatsApp, reconnectModalOpen, reconnectingWhatsApp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ LISTENER SECUNDARIO: PLAN_LIMIT, badSession, reconnected, update general
  useEffect(() => {
    if (!socketManager || !companyId) return;

    const socket = socketManager.getSocket(companyId);
    if (!socket) return;

    const handleSocketWhatsAppUpdate = (data) => {
      if (data && data.action === "error" && data.error) {
        if (data.error.code === "PLAN_LIMIT_REACHED") {
          toast.error(data.error.message || "Has alcanzado el límite de conexiones de tu plan. Elimina conexiones desconectadas o contacta soporte para upgrade.", {
            autoClose: 8000,
          });
          refreshWhatsApps();
          return;
        }
      }

      // Sin aviso propio: el backend emite `reconnected` y `connected` para la MISMA
      // reconexión, y el listener global de useWhatsApps ya avisa con «WhatsApp
      // conectado — {nombre}» (un toastId por conexión). Avisar aquí también daba dos
      // notificaciones por conexión — doce con seis conexiones.
      if (data.action === "reconnected" && data.whatsapp) {
        refreshWhatsApps();
        return;
      }

      if (data.action === "disconnected" && data.whatsapp) {
        const wa = data.whatsapp;
        const localWa = whatsApps.find(w => w.id === wa.id);
        const isBadSession = !!(localWa?.canCleanSession);

        if (isBadSession) {
          const whatsappName = wa.name || `WhatsApp ${wa.id}`;
          // Mismo toastId que el «Sin conexión» del listener global: este mensaje es
          // más útil (permite limpiar la sesión con un clic), así que lo sustituye en
          // vez de sumarse. Una sola notificación por conexión.
          const toastId = `wa-disconnected-${wa.id}`;
          const opciones = {
            autoClose: 10000,
            onClick: () => {
              handleCleanAndReconnect(wa.id);
            }
          };
          const texto = `⚠️ ${whatsappName} desconectado - Sesión corrupta detectada`;
          if (toast.isActive(toastId)) {
            toast.update(toastId, { render: texto, type: "error", ...opciones });
          } else {
            toast.error(texto, { toastId, ...opciones });
          }
          refreshWhatsApps();
          return;
        }
      }

      const whatsappData = data.whatsapp || data.session;

      if (data.action === "update" && whatsappData) {
        refreshWhatsApps();
      }

      if (data.action === "connected" && whatsappData) {
        refreshWhatsApps();
      }
    };

    socket.on(`company-${companyId}-whatsapp`, handleSocketWhatsAppUpdate);

    return () => {
      socket.off(`company-${companyId}-whatsapp`, handleSocketWhatsAppUpdate);
    };
  }, [socketManager, companyId, refreshWhatsApps, handleCleanAndReconnect, whatsApps]);

  // ✅ LISTENER DE ALERTAS DE RECURSOS DEL SERVIDOR
  useEffect(() => {
    if (!socketManager || !companyId) return;

    const socket = socketManager.getSocket(companyId);
    if (!socket) return;

    const handleResourceAlert = (data) => {
      if (data.action === 'resource_alert' && data.alert) {
        const alert = data.alert;
        setResourceAlerts(prev => [...prev.slice(-4), alert]);
        showResourceNotification(alert);
      }
    };

    socket.on(`company-${companyId}-resource-alert`, handleResourceAlert);

    return () => {
      socket.off(`company-${companyId}-resource-alert`, handleResourceAlert);
    };
  }, [socketManager, companyId, showResourceNotification]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useConnectionsSocket;
